// Mock data for Yourweddingfriend.com - frontend only

export const features = [
  {
    id: 'wedding-dashboard',
    mockup: 'MockWeddingDashboard',
    eyebrow: 'Wedding Dashboard',
    title: 'Every number that matters, in one calm view.',
    description: "Total headcount, RSVPs coming in, meal breakdown, email delivery health. See who opened the invite but hasn't replied — and nudge them with one tap.",
  },
  {
    id: 'invitations',
    mockup: 'MockEmailInvitation',
    eyebrow: 'Email + SMS invitations',
    title: 'Invites that actually look like an invitation.',
    description: 'Table-styled HTML emails (four palettes, couple photo, venue address, hotels block) plus matching text messages that guests can tap to open the RSVP page. Send both, from one screen.',
  },
  {
    id: 'rsvp-page',
    mockup: 'MockRsvpPage',
    eyebrow: 'RSVP page you control',
    title: 'Ask exactly what you need. Nothing more.',
    description: "Customize every question — plus-ones, meal, dietary, custom fields. Add a venue photo so guests recognize the spot. Missing an email? We capture it at RSVP time and auto-send the full invite.",
  },
  {
    id: 'guests-list',
    mockup: 'MockGuestsList',
    eyebrow: 'Guest list that pays attention',
    title: "Pill badges for what's missing. One tap to fix it.",
    description: "Instantly see who's still missing an email or phone, filter to just them, and text a friendly nudge. Delivery status, meal choice and dietary needs live right on every row.",
  },
  {
    id: 'ai-friend',
    mockup: 'MockAiFriend',
    eyebrow: 'AI Wedding Friend',
    title: 'A thoughtful planner on call at 1am.',
    description: 'Drafts FAQ text, invitation copy, save-the-date wording and the awkward parent conversation. Knows your dates, budget, venue and guest list — so answers are actually specific to you.',
  },
  {
    id: 'seating',
    mockup: 'MockSeating',
    eyebrow: 'Seating planner',
    title: 'Seat charts that survive Aunt Linda RSVPing late.',
    description: 'Drop guests onto tables, reshuffle in seconds, and never start from scratch. Visual, forgiving, and honestly a little bit magical.',
  },
  {
    id: 'budget',
    mockup: 'MockBudget',
    eyebrow: 'Budget + timeline',
    title: 'Live spend alerts. Every deadline sequenced.',
    description: "See what's spent, what's left, and where the next dollar should go. Alerts catch overspend the moment it starts, and the timeline sequences every fitting and deposit backwards from your date.",
  }
];

export const bonuses = [
  {
    title: 'Your Wedding Friend, on call',
    badge: 'Most Loved',
    description: 'A thoughtful AI planner who knows your dates, budget and decisions — ready whenever the 1am questions hit.',
    points: ['No more late-night Googling', 'Real, specific suggestions', 'Always free, always patient']
  },
  {
    title: 'The Vendor Interview Kit',
    description: 'A curated set of questions, red-flag checks and negotiation prompts so you never walk into a meeting unprepared.',
    points: ['80+ questions across categories', 'Compare quotes side by side', 'Save thousands on the right “yes”']
  },
  {
    title: 'Save-the-Date Starter Pack',
    description: 'Twelve refined, modern templates ready for print or text — fully editable, beautifully spaced.',
    points: ['Classic, modern and editorial styles', 'Email, paper and Instagram-ready', 'Edit in your browser, no tools needed']
  },
  {
    title: 'Post-Wedding Honeymoon Plan',
    description: 'The same calm system, repurposed for the trip after — budget, bookings, packing, the works.',
    points: ['Honeymoon budget tracker', 'Day-by-day itinerary builder', 'Smart packing checklist']
  }
];

export const howItWorks = [
  { step: 1, title: 'Start your account', text: 'Sign up in under a minute — no card required, no fine print.' },
  { step: 2, title: 'Share the basics', text: 'Date, venue, budget, headcount. We’ll set up the scaffolding.' },
  { step: 3, title: 'Make it yours', text: 'Pick a palette, tweak categories, prioritize what matters.' },
  { step: 4, title: 'Plan, breathe, repeat', text: 'Follow the rhythm. Hit milestones. Enjoy the engagement.' }
];

export const testimonials = [
  { name: 'Sophia & Marc', location: 'Asheville, NC', rating: 5, text: 'We were eight months out and completely overwhelmed. Two days in, our budget was sorted and we finally knew what to book next. Genuine life saver.' },
  { name: 'Emma & Liam', location: 'Brooklyn, NY', rating: 5, text: 'The AI friend is worth the price ten times over. Answered every weird vendor question I had at 1am the night before our tasting. I felt heard.' },
  { name: 'Aisha & Daniel', location: 'Austin, TX', rating: 5, text: 'We saved over $3,200 just by listening to the budget alerts and using the vendor comparison kit. Absurdly good ROI.' },
  { name: 'Chiara & Luca', location: 'Chicago, IL', rating: 5, text: 'Seating used to make me cry. Now it takes twenty minutes. The drag-around tables and smart grouping are pure magic.' },
  { name: 'Priya & Arjun', location: 'Seattle, WA', rating: 5, text: 'Multi-day, multi-event, 480 guests across two countries — it handled all of it. Even my mother-in-law was impressed. Unheard of.' },
  { name: 'Hannah & Theo', location: 'Denver, CO', rating: 5, text: 'The day-of timeline literally saved our reception. Our coordinator printed it and everyone knew exactly what to do. Effortless.' }
];

export const faqs = [
  { q: 'Will I actually be able to figure this out?', a: 'Yes — the system is already built; you just fill in your details. No tutorials to watch, no setup required. You’re guided step by step and you can’t really break anything.' },
  { q: 'Do I pay every month?', a: 'No. We have a generous free tier you can use forever, and our paid plans are one-time purchases — not subscriptions. No surprise charges, no "per guest" fees, no domain upsells.' },
  { q: 'Will it work on my phone?', a: 'Yep. Everything lives in the cloud. Open it on laptop, tablet or phone in any modern browser — Mac, Windows, iOS or Android.' },
  { q: 'I’m honestly not organized. Will this still help?', a: 'Especially then. The structure does the heavy lifting for you. You just add your details and follow the rhythm — it’s genuinely hard to forget anything important.' },
  { q: 'What if we get stuck?', a: 'Your AI Wedding Friend is on call 24/7, and our human team replies within hours via email — weekends included. We don’t leave couples hanging.' },
  { q: 'What if it’s not for us?', a: 'A 30-day no-questions-asked refund. If it isn’t the right fit, just tell us and we’ll send your money back. Easy.' }
];

export const pricing = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'For couples just exploring',
    price: 0,
    oldPrice: null,
    features: [
      'Up to 50 guests',
      'RSVP page + wedding website',
      'Guest list, budget & timeline',
      'AI Wedding Friend (5 chats/day)',
      'CSV import & export',
      'Manual RSVP only — no email or SMS sends',
    ],
    cta: 'Start free',
    accent: false,
  },
  {
    id: 'essentials',
    name: 'Essentials',
    tagline: 'Beautiful email invitations',
    price: 49,
    oldPrice: 99,
    features: [
      'Up to 100 guests',
      'Email invitations (up to 300 sends)',
      'Save-the-Date digital send',
      '4 email design palettes',
      'Wedding dashboard + delivery tracking',
      'Seating chart + printable table & menu cards',
      'AI Wedding Friend (unlimited)',
      'Custom URL',
    ],
    cta: 'Get Essentials',
    accent: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'Everything + SMS invitations',
    price: 99,
    oldPrice: 199,
    features: [
      'Everything in Essentials',
      'Up to 250 guests',
      'SMS invitations (up to 500 sends)',
      'One-tap Auto-Nudge to non-repliers',
      'Two-way SMS RSVP (guests text back)',
      'Custom email + SMS templates',
      'Remove Yourweddingfriends branding',
      '5 physical Save-the-Date postcards included',
      'Priority support (24h response)',
    ],
    cta: 'Go Premium',
    accent: true,
    badge: 'Most Loved',
  },
  {
    id: 'ultimate',
    name: 'Ultimate',
    tagline: 'White-glove concierge',
    price: 199,
    oldPrice: 349,
    features: [
      'Everything in Premium',
      'Unlimited guests',
      'Concierge SMS setup — dedicated wedding phone number',
      'Unlimited email + SMS sends',
      '25 physical Save-the-Date postcards included',
      'Guest photo album (shared upload)',
      '1:1 planner call (30-min video)',
      'Custom subdomain (yourname.yourweddingfriends.com)',
      'Data export (JSON) + white-label branding',
    ],
    cta: 'Go Ultimate',
    accent: false,
  },
];

// Dashboard mock data (legacy seed, no longer used at runtime — kept for reference / aiSuggestions)
export const aiSuggestions = [
  'How much should we budget for flowers for 120 guests?',
  'What should I ask a wedding photographer before booking?',
  'Help me write a save-the-date message for our wedding.',
  'How do I politely tell guests no kids at the wedding?'
];
